import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ScrollService {
  private readonly defaultHeaderHeight = 52;
  private readonly defaultHeaderPadding = 20;
  private readonly defaultHashDelayMs = 150;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  observeActiveSection(onActiveSectionChange: (id: string) => void,
    options?: { selector?: string }): () => void {

    if (!isPlatformBrowser(this.platformId)){
      return () => {};
    } 

    const windowRef = this.document.defaultView;

    if (!windowRef){
      return () => {}
    };

    const selector = options?.selector ?? 'section[id]';
    let sections: HTMLElement[] = [];

    try {
      sections = Array.from(this.document.querySelectorAll<HTMLElement>(selector));
    } catch {
      return () => {};
    }

    if (sections.length === 0){
       return () => {}
    };

    let lastActiveId = '';
    const homeSection = sections.find((section) => section.id === 'home');

    const getHeaderOffset = () => {
      const header = this.document.querySelector('header');
      const headerHeight = header ? header.clientHeight : this.defaultHeaderHeight;
      return headerHeight + this.defaultHeaderPadding;
    };

    const computeActiveSection = () => {
      const headerOffset = getHeaderOffset();
      
      if (windowRef.scrollY <= headerOffset && homeSection) {
        if (lastActiveId !== homeSection.id) {
          lastActiveId = homeSection.id;
          onActiveSectionChange(homeSection.id);
        }

        return;
      }

      let closestSection: HTMLElement | null = null;
      let minDistance = Number.POSITIVE_INFINITY;

      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        const distanceToCenter = Math.abs(rect.top + rect.height / 2 - windowRef.innerHeight / 2);

        if (distanceToCenter < minDistance && rect.top < windowRef.innerHeight) {
          minDistance = distanceToCenter;
          closestSection = section;
        }
      }

      if (!closestSection){
        return;
      };

      const id = closestSection.id;
      if (id && id !== lastActiveId) {
        lastActiveId = id;
        onActiveSectionChange(id);
      }
    };

    let scheduled = false;
    const scheduleCompute = () => {
      if (scheduled) return;
      scheduled = true;
      windowRef.requestAnimationFrame(() => {
        scheduled = false;
        computeActiveSection();
      });
    };

    windowRef.addEventListener('scroll', scheduleCompute, { passive: true });
    windowRef.addEventListener('resize', scheduleCompute, { passive: true });

    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in windowRef) {
      observer = new IntersectionObserver(() => scheduleCompute(), {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      sections.forEach((section) => observer?.observe(section));
    }

    // Initial sync
    scheduleCompute();

    return () => {
      windowRef.removeEventListener('scroll', scheduleCompute);
      windowRef.removeEventListener('resize', scheduleCompute);
      observer?.disconnect();
    };
  }

  initScrollBehavior(): void {
    if (!isPlatformBrowser(this.platformId)){
       return;
    };

    const windowRef = this.document.defaultView;

    if (!windowRef){
       return
    };

    if ('scrollRestoration' in windowRef.history) {
      windowRef.history.scrollRestoration = 'manual';
    }
  }

  scrollToFragment(
    fragment: string,
    headerSelector = 'header',
    offsetFallback = this.defaultHeaderHeight,
    options?: { delayMs?: number }
  ): void {
    this.scrollToFragmentInternal(fragment, headerSelector, offsetFallback, options);
  }

  private scrollToFragmentInternal(
    fragment: string,
    headerSelector = 'header',
    offsetFallback = this.defaultHeaderHeight,
    options?: { delayMs?: number; waitForLoad?: boolean }): void {
    if (!isPlatformBrowser(this.platformId)){
       return;
    };

    const windowRef = this.document.defaultView;

    if (!windowRef){
       return;
    };

    if (!fragment){
       return;
    };

    const normalizedFragment = fragment.replace(/^#/, '');

    const scrollToTarget = () => {
      if (normalizedFragment === 'home') {
        windowRef.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        return;
      }

      const element = this.document.getElementById(normalizedFragment);
      if (!element) return;

      const header = this.document.querySelector(headerSelector);
      const offset = header ? -header.clientHeight : -offsetFallback;
      const y = element.getBoundingClientRect().top + windowRef.scrollY + offset;
      windowRef.scrollTo({ top: y, behavior: 'smooth' });
    };

    const delayMs = options?.delayMs ?? 0;
    const schedule = () => {
      if (delayMs > 0) {
        setTimeout(scrollToTarget, delayMs);
        return;
      }
      scrollToTarget();
    };

    if (options?.waitForLoad && this.document.readyState !== 'complete') {
      windowRef.addEventListener('load', () => windowRef.requestAnimationFrame(schedule), {
        once: true,
      });
      
      return;
    }

    if (options?.waitForLoad) {
      windowRef.requestAnimationFrame(schedule);
      return;
    }

    schedule();
  }
}
